import {last, omit} from 'lodash';
import {BaseClass} from '@teemill/common/classes';
import {Person} from '@teemill/common/models';
import {dayjs, snackbar} from '@teemill/common/services';
import {textContent} from '@teemill/utilities';
import {formatUrl} from '@teemill/common/helpers';

export default class ChatMessage extends BaseClass {
  constructor({
    id,
    body,
    url,
    likes,
    images = [],
    files = [],
    toxicity,
    isToxic,
    parent,
    createdAt,
    filteredAt,
    refocusedAt,
    quality,
    owner,
    unread,
    commentable,
    replies = [],
    tasks = [],
    feedback = [],
  } = {}) {
    super();

    this.id = id;
    this.body = body;
    this.url = url;
    this.createdAt = dayjs(createdAt);
    this.filteredAt = filteredAt ? dayjs(filteredAt) : null;
    this.refocusedAt = refocusedAt ? dayjs(refocusedAt) : null;
    this.quality = quality;
    this.owner = Person.map(owner);
    this.likes = likes;
    this.images = images;
    this.files = files;
    this.toxicity = toxicity;
    this.isToxic = isToxic;
    this.parent = parent;
    this.unread = unread;
    this.commentable = commentable;
    this.feedback = feedback;

    this.tasks = tasks.map(task => ({
      id: task.id,
      status: task.status,
      url: task.url,
      assignee: Person.map(task.assignee),
    }));

    this.replies = replies.map(reply =>
      ChatMessage.map({
        ...reply,
        parent: omit(this, 'replies'),
      })
    );
  }

  /**
   * @returns {String}
   */
  get createdAtDiff() {
    return this.createdAt.fromNow();
  }

  /**
   * @returns {Number}
   */
  get timestamp() {
    return this.createdAt.valueOf();
  }

  /**
   * Get all images and files relating to a message
   *
   * @return {Array}
   */
  get media() {
    return [...this.images, ...this.files];
  }

  /**
   * @return {Number}
   */
  get replyCount() {
    return this.replies.length;
  }

  /**
   * @return {Boolean}
   */
  get hasReplies() {
    return !!this.replies.length;
  }

  /**
   * @return {Boolean}
   */
  get hasContent() {
    return this.body.length > 0 && this.body !== '<p></p>';
  }

  /**
   * @return {Boolean}
   */
  get isReply() {
    return !!this.parent;
  }

  /**
   * @return {ChatMessage}
   */
  get lastReply() {
    if (!this.hasReplies) {
      return null;
    }

    return last(this.replies);
  }

  /**
   * @return {Boolean}
   */
  get hasUnreadReplies() {
    return this.unreadReplies.length > 0;
  }

  /**
   * @return {ChatMessage[]}
   */
  get unreadReplies() {
    return this.replies.filter(reply => reply.unread);
  }

  /**
   * @return {ChatMessage[]}
   */
  get readReplies() {
    return this.replies.filter(reply => !reply.unread);
  }

  /**
   * @return {String}
   */
  get el() {
    return window.document.getElementById(this.id);
  }

  /**
   * @return {Number}
   */
  get likeCount() {
    return this.likes.length;
  }

  /**
   * @return {Boolean}
   */
  get shouldHide() {
    return this.isToxic || !!this.filteredAt;
  }

  /**
   * @return {Boolean}
   */
  get isHighlighted() {
    return this.quality === 100;
  }

  /**
   * Strip HTML tags and return the body
   *
   * @return {String}
   */
  get bodyAsPlain() {
    return textContent(this.body);
  }

  /**
   * @return {Boolean}
   */
  get hasTasks() {
    return this.tasks.length > 0;
  }

  /**
   * @return {Number}
   */
  get normalizedToxicity() {
    return Math.round(this.toxicity * 100);
  }

  /**
   * @return {String}
   */
  get ownerAvatar() {
    if (!this.owner || !this.owner.avatar) {
      return null;
    }

    return this.owner.avatar.size('thumbnail');
  }

  /**
   * Get the parent if this is a reply, otherwise return self.
   *
   * @return {ChatMessage|Number}
   */
  get base() {
    return this.isReply ? this.parent : this;
  }

  /**
   * @param {Array} siblings
   *
   * @return {Boolean}
   */
  isChained(siblings) {
    const isFirstSibling = siblings.findIndex(i => i.id === this.id) === 0;

    // If the message is the first reply to a message
    // and was posted by the same owner - then consider it chained
    if (isFirstSibling && this.parent) {
      return this.haveSameOwner(this.parent);
    }

    const index = siblings.findIndex(m => m.id === this.id);
    const previousMessage = siblings[index - 1];

    // Only consider messages chained if the previous message
    // is not hidden - this saves some complex logic around checking
    // only previous siblings that are not hidden
    if (previousMessage && !previousMessage.shouldHide) {
      return this.haveSameOwner(previousMessage);
    }

    return false;
  }

  /**
   * Does the provided message have the same owner as the current instance
   *
   * @param {ChatMessage} message
   *
   * @return {Boolean}
   */
  haveSameOwner(message) {
    if (!message.owner || !this.owner) {
      return false;
    }

    return message.owner.id === this.owner.id;
  }

  /**
   * @param {User} user
   *
   * @return {Boolean}
   */
  ownedBy(user) {
    if (!user || !this.owner) {
      return false;
    }

    return user.id === this.owner.id;
  }

  /**
   * Mark this message as having been read.
   *
   * ? This does not update the API state for the message
   * ? or it's associated channel
   *
   * @return {ChatMessage}
   */
  markAsRead() {
    this.unread = false;

    this.markRepliesAsRead();

    return this;
  }

  /**
   * Mark this message's replies as having been read.
   *
   * ? This does not update the API state for the message
   * ? or it's associated channel
   *
   * @return {ChatMessage}
   */
  markRepliesAsRead() {
    this.replies.forEach(reply => {
      reply.markAsRead();
    });

    return this;
  }

  /**
   * @param {Number} id
   *
   * @param {Promise<ChatMessage>} withMeta whether or not to include pagination metadata
   */
  static get(id, {withMeta = true} = {}) {
    return $axios.get(formatUrl(`/omnis/v3/chat/message/show/${id}/`), {
      params: {withMeta},
    });
  }

  /**
   * @param {Object}
   *
   * @return {Promise<ChatMessage>}
   */
  static create({body, channel, parent, files}) {
    return $axios
      .post(formatUrl('/omnis/v3/chat/message/create/'), {
        body,
        channel,
        files,
        parent: parent || undefined,
      })
      .success(message => ChatMessage.map(message))
      .handle(403, () => {
        snackbar.error('Sorry, this is a private channel!');
      })
      .validation()
      .oops()
      .output();
  }

  /**
   * @return {Promise}
   */
  delete() {
    return $axios
      .post(formatUrl(`/omnis/v3/comment/delete/${this.id}/`))
      .handle(403, () => {
        snackbar.error('Permission denied');
      });
  }

  /**
   * @return {Promise}
   */
  react(reaction) {
    return $axios
      .post(formatUrl(`/omnis/v3/comment/react/${this.id}/${reaction}`))
      .handle(403, () => {
        snackbar.error('Permission denied');
      })
      .oops();
  }

  /**
   * @return {Promise}
   */
  hide() {
    return $axios
      .post(formatUrl(`/omnis/v3/comment/hide/${this.id}/`))
      .handle(403, () => {
        snackbar.error('Permission denied');
      })
      .oops();
  }

  /**
   * @return {Promise}
   */
  unhide() {
    return $axios
      .post(formatUrl(`/omnis/v3/comment/unhide/${this.id}/`))
      .handle(403, () => {
        snackbar.error('Permission denied');
      })
      .oops();
  }

  /**
   * @return {Promise}
   */
  assignTo({id}) {
    return $axios
      .post(formatUrl(`/omnis/v3/comment/assign/${this.id}/`), {user: id})
      .handle(403, () => {
        snackbar.error('Permission denied');
      })
      .oops();
  }

  /**
   * @return {Promise}
   */
  triage() {
    return $axios
      .post(formatUrl(`/omnis/v3/comment/triage/${this.id}/`))
      .handle(403, error => {
        snackbar.error(error.message);
      })
      .oops();
  }

  /**
   * @return {Promise}
   */
  highlight() {
    return $axios
      .post(formatUrl(`/omnis/v3/comment/highlight/${this.id}/`))
      .handle(403, () => {
        snackbar.error('Permission denied');
      })
      .oops();
  }

  /**
   * @return {Promise}
   */
  unhighlight() {
    return $axios
      .post(formatUrl(`/omnis/v3/comment/unhighlight/${this.id}/`))
      .handle(403, () => {
        snackbar.error('Permission denied');
      })
      .oops();
  }

  /**
   * @return {Promise}
   */
  refocus() {
    return $axios
      .post(formatUrl(`/omnis/v3/comment/refocus/${this.id}/`))
      .handle(403, () => {
        snackbar.error('Permission denied');
      })
      .oops();
  }

  /**
   * @param {Person} user
   *
   * @return {Boolean}
   */
  hasLiked(user) {
    return this.likes.some(liker => liker.id === user.id);
  }
}
